import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Globe, Image as ImageIcon, Phone, Mail, Building, Car, Plane, Map, Pen } from 'lucide-react';
import {
    Code, Wrench, Tag, Zap, Home, Locate, CalendarPlus, History
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';

import ActivityView from '../Product/views/ActivityView';
import HotelView from '../Product/views/HotelView';
import FlightView from '../Product/views/FlightView';
import TransferView from '../Product/views/TransfertView';

const ProductDetails = ({ lang }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(lang);

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const detail_url = process.env.REACT_APP_API_PRODUCT_URL;

    const fetchProductDetail = async () => {
        try {
            setLoading(true);
            const {data} = await api.get(`${detail_url}/${id}`);

            setProduct(data);

        } catch (error) {
            console.error('Erreur lors du chargement des détails:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductDetail();
    }, [id]);

    const getProductName = () => {
        if (!product) return t('notAvailable');
        return product[lang] || product.en || product.fr || product.es || t('notAvailable');
    };

    const getFieldValue = (fieldArray, fallback = '') => {
        if (!fieldArray || fieldArray.length === 0) return fallback;
        const value = fieldArray[0][lang] || fieldArray[0].en || fieldArray[0].fr || fieldArray[0].es;
        return value || fallback;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'published':
                return '#10b981';
            case 'draft':
                return '#6b7280';
            case 'archived':
                return '#ef4444';
            default:
                return '#6b7280';
        }
    };

    const getServiceIcon = (service) => {
        switch (service) {
            case 'hotel':
                return <Building size={16} />;
            case 'transfer':
                return <Car size={16} />;
            case 'flight':
                return <Plane size={16} />;
            case 'activity':
                return <Map size={16} />;
            default:
                return <Building size={16} />;
        }
    };

    const getChargeServiceInfo = () => {
        if (!product) return { text: '', color: '' };

        const charge = product.charge_service || 0;
        const type = product.charge_service_type || 'out';

        if (charge === 0) return { text: t('noServiceFee'), color: '#10b981' };

        const percentage = `${charge}%`;
        if (type === 'in') {
            return {
                text: `${percentage} ${t('includedInPrice')}`,
                color: '#3b82f6'
            };
        } else {
            return {
                text: `${percentage} ${t('additionalFee')}`,
                color: '#f59e0b'
            };
        }
    };

    const styles = {
        container: {
            padding: '12px 24px 24px',
            background: '#f9fafb',
            minHeight: '100vh'
        },
        header: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '12px',
            gap: '8px',
            flexWrap: 'wrap'
        },
        headerMain: {
            flex: 1,
            minWidth: '300px'
        },
        headerActions: {
            width: '220px',
            minWidth: '220px',
            alignItems: 'right',
            justifyContent: 'right'
        },
        titleSection: {
            marginBottom: '16px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: '0px 0px 8px'
        },
        subtitle: {
            fontSize: '14px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '8px'
        },
        statusBadge: {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            textTransform: 'uppercase'
        },
        quickInfo: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap'
        },
        infoBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '13px'
        },
        badgeLabel: {
            fontWeight: '600',
            color: '#374151'
        },
        badgeValue: {
            color: '#6b7280'
        },
        serviceFeeBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600'
        },
        quickActionsCard: {
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            // marginBottom: '8px'
        },
        actionButtons: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        secondaryButton: {
            padding: '10px 16px',
            background: '#47b691',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            width: '100%',
            fontSize: '14px'
        },
        backButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            cursor: 'pointer',
            color: '#374151',
            textDecoration: 'none'
        },
        loading: {
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
        },
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            marginBottom: '5px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
        },
        infoCard: {
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        infoItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0px 0px',
            borderBottom: '1px solid #f3f4f6'
        },
        infoLabel: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
        },
        infoValue: {
            color: '#6b7280',
            fontSize: '14px',
            textAlign: 'right'
        },
        twoColumnsLayout: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            alignItems: 'start',
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
        },
        columnTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0px 0px 3px'
        },
        supplierCard: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            flex: 1
        },
        supplierInfo: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        },
        supplierName: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937'
        },
        supplierId: {
            fontSize: '14px',
            color: '#6b7280'
        },
        primaryButton: {
            padding: '10px 20px',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            textAlign: 'center',
            marginBottom: '18px'
        },
        policyCard: {
            padding: '20px',
            background: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
        },
        policyHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px'
        },
        policyCode: {
            padding: '4px 8px',
            background: '#1e40af',
            color: 'white',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600'
        },
        policyName: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e40af'
        },
        policyDescription: {
            color: '#374151',
            marginTop: '8px'
        },
        iconText: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        textContent: {
            color: '#6b7280',
            lineHeight: '1.6',
            whiteSpace: 'pre-line'
        },
        listContainer: {
            marginTop: '12px'
        },
        listItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 0',
            fontSize: '14px',
            color: '#374151',
            borderBottom: '1px solid #f3f4f6'
        },
        noData: {
            color: '#9ca3af',
            fontStyle: 'italic',
            textAlign: 'center',
            padding: '20px'
        }
    };

    const renderServiceView = () => {
        if (!product) return null;

        switch (product.service) {
            case 'activity':
                return <ActivityView product={product} lang={lang} />;
            case 'hotel':
                return <HotelView product={product} lang={lang} />;
            case 'flight':
                return <FlightView product={product} lang={lang} />;
            case 'transfer':
                return <TransferView product={product} lang={lang} />;
            default:
                return <div style={{ color: '#6b7280', padding: '20px', textAlign: 'center' }}>
                    {t('noServiceViewAvailable')}
                </div>;
        }
    };

    if (loading) {
        return <div style={styles.loading}>{t('loading')}...</div>;
    }

    if (!product) {
        return <div style={styles.loading}>{t('productNotFound')}</div>;
    }

    const chargeInfo = getChargeServiceInfo();

    return (
        <div style={styles.container}>
            {/* Header avec bouton retour */}
            <div style={styles.header}>
                <button
                    style={styles.backButton}
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                    {t('back')}
                </button>
            </div>
            {/* Header avec informations principales et actions */}
            <div style={styles.header}>
                <div style={styles.headerMain}>
                    <div style={styles.titleSection}>
                        <h1 style={styles.title}>{getProductName()}</h1>
                        <div style={styles.subtitle}>
                            {getServiceIcon(product.service)}
                            <span>ID: {product.id} • {t(product.service)} • {t(product.status)}</span>
                            <span style={{
                                ...styles.statusBadge,
                                background: getStatusColor(product.status)
                            }}>
                                {t(product.status)}
                            </span>
                        </div>
                    </div>

                    {/* Informations importantes intégrées dans le header */}
                    <div style={styles.quickInfo}>
                        <div style={styles.infoBadge}>
                            <span style={styles.badgeLabel}>{t('completion')}:</span>
                            <span style={styles.badgeValue}>{product.completion_percentage}%</span>
                        </div>
                        <div style={styles.infoBadge}>
                            <span style={styles.badgeLabel}>{t('serviceFee')}:</span>
                            <span style={{
                                ...styles.serviceFeeBadge,
                                background: chargeInfo.color,
                                color: 'white'
                            }}>
                                {chargeInfo.text}
                            </span>
                        </div>
                        <div style={styles.infoBadge}>
                            <span style={styles.badgeLabel}>{t('bookingMode')}:</span>
                            <span style={styles.badgeValue}>{t(product.booking_confirmation_mode)}</span>
                        </div>
                        <div style={styles.infoBadge}>
                            <span style={styles.badgeLabel}>{t('provider')}:</span>
                            <span style={styles.badgeValue}>ID: {product.provider_id}</span>
                        </div>
                    </div>
                </div>

                {/* Actions rapides à droite */}
                <div style={styles.headerActions}>
                    <div style={styles.quickActionsCard}>
                        <h3 style={styles.columnTitle}>{t('supplier')}</h3>
                        <div style={styles.subtitle}> ID: {product.provider_id} </div>
                        <div style={styles.actionButtons}>
                            <button
                                style={styles.primaryButton}
                                onClick={() => navigate(`/suppliers/${product.provider_id}`)}
                            >
                                {t('viewSupplier')}
                            </button>
                        </div>
                    </div>
                </div>
                <div style={styles.headerActions}>
                    <div style={styles.quickActionsCard}>
                        <h3 style={styles.columnTitle}>{t('quickActions')}</h3>
                        <div style={styles.actionButtons}>
                            <button
                                style={styles.secondaryButton}
                                onClick={() => navigate(`/bookings/${product.id}`)}
                            >
                                <Calendar size={16} />
                                {t('viewBookings')}
                            </button>
                            <button
                                style={styles.secondaryButton}
                                onClick={() => console.log('Éditer le produit', product.id)}
                            >
                                <Pen size={16} />
                                {t('editProduct')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Informations générales */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>{t('generalInformation')}</h2>
                </div>

                <div style={styles.grid}>
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '8px', color: '#1f2937' }}>{t('basicInfo')}</h3>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <Code size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                {t('productId')}</span>
                            <span style={styles.infoValue}>
                                {product.id}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <Wrench size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                {t('serviceType')}</span>
                            <span style={styles.infoValue}>

                                {t(product.service)}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <Tag size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />

                                {t('status')}</span>
                            <span style={styles.infoValue}>
                                {t(product.status)}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>
                                <Zap size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                {t('active')}</span>
                            <span style={styles.infoValue}>

                                {product.is_active ? t('yes') : t('no')}
                            </span>
                        </div>
                    </div>

                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('location')}</h3>
                        {product.address && (
                            <div style={styles.iconText}>
                                <Home size={16} style={{ marginRight: '8px', color: '#666' }} />
                                <span style={styles.infoValue}>{product.address}</span>
                            </div>
                        )}
                        {product.city_id && (
                            <div style={styles.iconText}>
                                <MapPin size={16} style={{ marginRight: '8px', color: '#666' }} />
                                <span style={styles.infoValue}>{t('city')}: {product.city_id}</span>
                            </div>
                        )}
                        {product.country_id && (
                            <div style={styles.iconText}>
                                <Globe size={16} style={{ marginRight: '8px', color: '#666' }} />
                                <span style={styles.infoValue}>{t('country')}: {product.country_id}</span>
                            </div>
                        )}
                        {product.latitude && product.longitude && (
                            <div style={styles.iconText}>
                                <Locate size={16} style={{ marginRight: '8px', color: '#666' }} />
                                <span style={styles.infoValue}>Lat: {product.latitude}, Lng: {product.longitude}</span>
                            </div>
                        )}
                    </div>

                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('dates')}</h3>
                        <div style={styles.iconText}>
                            <CalendarPlus size={16} style={{ marginRight: '8px', color: '#666' }} />
                            <span style={styles.infoValue}>{t('created')}: {new Date(product.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                        </div>
                        <div style={styles.iconText}>
                            <History size={16} style={{ marginRight: '8px', color: '#666' }} />
                            <span style={styles.infoValue}>{t('updated')}: {new Date(product.updated_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                        </div>
                        {product.phone && (
                            <div style={styles.iconText}>
                                <Phone size={16} style={{ marginRight: '8px', color: '#666' }} />
                                <span style={styles.infoValue}>{product.phone}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Description et Résumé */}
            {(product.short_summary && product.short_summary.length > 0) ||
                (product.descriptions && product.descriptions.length > 0) ? (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>{t('description')}</h2>
                    <div style={styles.grid}>
                        {product.short_summary && product.short_summary.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('shortSummary')}</h3>
                                <p style={styles.textContent}>
                                    {getFieldValue(product.short_summary, t('noSummary'))}
                                </p>
                            </div>
                        )}
                        {product.descriptions && product.descriptions.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('fullDescription')}</h3>
                                <p style={styles.textContent}>
                                    {getFieldValue(product.descriptions, t('noDescription'))}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Vue spécifique au service */}
            {renderServiceView()}

            {/* Section Inclus/Exclus */}
            {/* {(product.included && product.included.length > 0) ||
                (product.excluded && product.excluded.length > 0) ? (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>{t('inclusionsExclusions')}</h2>
                    <div style={styles.grid}>
                        {product.included && product.included.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('included')}</h3>
                                <div style={styles.listContainer}>
                                    {product.included.map((item, index) => (
                                        <div key={index} style={styles.listItem}>
                                            <span>✓</span>
                                            <span>{getFieldValue([item])}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {product.excluded && product.excluded.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('excluded')}</h3>
                                <div style={styles.listContainer}>
                                    {product.excluded.map((item, index) => (
                                        <div key={index} style={styles.listItem}>
                                            <span>✗</span>
                                            <span>{getFieldValue([item])}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null} */}

            {/* Section Éléments requis/interdits */}
            {/* {(product.required_items && product.required_items.length > 0) ||
                (product.prohibited_items && product.prohibited_items.length > 0) ? (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>{t('requirements')}</h2>
                    <div style={styles.grid}>
                        {product.required_items && product.required_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('requiredItems')}</h3>
                                <div style={styles.listContainer}>
                                    {product.required_items.map((item, index) => (
                                        <div key={index} style={styles.listItem}>
                                            <span>{getFieldValue([item])}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {product.prohibited_items && product.prohibited_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#1f2937' }}>{t('prohibitedItems')}</h3>
                                <div style={styles.listContainer}>
                                    {product.prohibited_items.map((item, index) => (
                                        <div key={index} style={styles.listItem}>
                                            <span>{getFieldValue([item])}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null} */}

            {/* Section Informations supplémentaires */}
            {product.additional_info.en && product.additional_info.length > 0 && (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>{t('additionalInfo')}</h2>
                    <p style={styles.textContent}>
                        {getFieldValue(product.additional_info)}
                    </p>
                </div>
            )}

            {/* Section Points clés */}
            {product.key_points && product.key_points.length > 0 && (
                <div style={styles.grid}>
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>{t('keyPoints')}</h2>
                        <div style={styles.listContainer}>
                            {product.key_points.map((point, index) => (
                                <div key={index} style={styles.listItem}>
                                    <span>{getFieldValue([point])}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Section Politique d'annulation */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>{t('cancellationPolicy')}</h2>
                        <div style={styles.policyCard}>
                            <div style={styles.policyHeader}>
                                <span style={styles.policyCode}>{product.cancellation_policy_code}</span>
                                <span style={styles.policyName}>
                                    {t(product.cancellation_policy_code === '24H' ? '24HourPolicy' : 'flexiblePolicy')}
                                </span>
                            </div>
                            <p style={styles.policyDescription}>
                                {product.cancellation_policy_percentage > 0
                                    ? `${t('cancellationFee')}: ${product.cancellation_policy_percentage}%`
                                    : t('freeCancellation')}
                            </p>
                        </div>
                    </div>

                </div>
            )}



            {/* Section Fournisseur et Actions rapides */}
            {/* <div style={styles.section}>
                <div style={styles.twoColumnsLayout}>
                    <div style={styles.column}>
                        <h3 style={styles.columnTitle}>{t('supplier')}</h3>
                        <div style={styles.supplierCard}>
                            <div style={styles.supplierInfo}>
                                <div style={styles.supplierName}>{t('provider')} #{product.provider_id}</div>
                                <div style={styles.supplierId}>ID: {product.provider_id}</div>
                            </div>
                            <button
                                style={styles.primaryButton}
                                onClick={() => navigate(`/providers/${product.provider_id}`)}
                            >
                                {t('viewSupplier')}
                            </button>
                        </div>
                    </div>

                    <div style={styles.column}>
                        <h3 style={styles.columnTitle}>{t('quickActions')}</h3>
                        <div style={styles.supplierCard}>
                            <div style={styles.actionButtons}>
                                <button
                                    style={styles.secondaryButton}
                                    onClick={() => navigate(`/bookings?product_id=${product.id}`)}
                                >
                                    <Calendar size={16} />
                                    {t('viewBookings')}
                                </button>
                                <button
                                    style={styles.secondaryButton}
                                    onClick={() => console.log('Éditer le produit', product.id)}
                                >
                                    <Pen size={16} />
                                    {t('editProduct')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    );
};

export default ProductDetails;